import { Box, Divider, Flex } from '@modusign/design-system';
import { LabelWithBookmarkResponse } from '@modusign/openapi-client-category/generated/model';
import { forwardRef, Ref, useEffect } from 'react';

import SearchInput from '@app/components/SearchInput';
import LabelButtonList from '@app/features/label/components/LabelPopovers/LabelSelectPopover/LabelButtonList';
import useLabelsFilters from '@app/features/label/hooks/useLabelsFilters';
import pushDataLayer from '@app/libs/analytics/googleTagManager';

import LabelListFilter from '../components/LabelListFilter';
import LabelSelectPopoverFooter from './LabelSelectPopoverFooter';
import LabelSelectPopoverHeader from './LabelSelectPopoverHeader';

/**
 * react-modal의 z-index 기본값이 1000입니다. 본 라벨팝오버도 1000으로 맞춰서 모달과 팝오버는 DOM에 렌더링된 순서로 그려지게 합니다.
 * e.g. 모달 밑에서 사용되는 팝오버 , 모달 위에서 사용하는 팝오버
 */
const MODAL_Z_INDEX = 1000;
interface LabelSelectPopoverProps {
  hidePopoverHeader?: boolean;
  hidePopoverFooter?: boolean;
  onClear?: () => void;
  onClose: () => void;
  onNonLabeledSelect?: () => void;
  onSelect: (label: LabelWithBookmarkResponse) => void;
}

const LabelSelectPopover = forwardRef(
  (
    {
      hidePopoverHeader = true,
      hidePopoverFooter,
      onClear,
      onClose,
      onNonLabeledSelect,
      onSelect,
      ...props
    }: LabelSelectPopoverProps,
    ref: Ref<HTMLDivElement>
  ) => {
    const [filters, { set, remove }] = useLabelsFilters();

    useEffect(() => {
      pushDataLayer({ event: 'viewLabelSelectPopover' });
    }, []);

    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor="boxBorderNeutralBase"
        borderRadius="4px"
        boxShadow="0px 3px 9px rgba(0, 0, 0, 0.1)"
        ref={ref}
        width={['100%', 'xxxs', 'xxxs']}
        zIndex={MODAL_Z_INDEX}
        {...props}
      >
        {!hidePopoverHeader && <LabelSelectPopoverHeader onClose={onClose} />}
        <Flex as="section" flexDirection="column" width="100%">
          <Flex as="section" flexDirection="column" gap="14px" p={4}>
            <SearchInput
              placeholder="이름으로 찾기"
              onChange={(event) => set('name', event.target.value)}
              onClear={() => remove('name')}
            />
            <LabelListFilter
              filters={filters}
              onBookmarkFirst={(bookmarkFirst) => set('bookmarkFirst', bookmarkFirst)}
              onSortByChange={(sortBy) => {
                set('sortBy', sortBy);
                set('order', sortBy === 'createdAt' ? 'DESC' : 'ASC');
              }}
            />
          </Flex>
          <Divider color="light" />
          <LabelButtonList
            {...filters}
            onClose={onClose}
            onNonLabeledSelect={onNonLabeledSelect}
            onSelect={onSelect}
          />
          <Divider color="light" />
        </Flex>
        {!hidePopoverFooter && <LabelSelectPopoverFooter onClear={onClear} />}
      </Box>
    );
  }
);

export default LabelSelectPopover;
